<template>
  <Content
    class="order-entry"
    :show-toolbar="!!selectedClient"
    remove-card-wrapper
  >
    <template #header>
      <RygenButton
        as="router-link"
        mode="tertiary"
        :to="{ name: 'orderList' }"
        :icon="PrimeIcons.ARROW_LEFT"
        :label="t('orders.backToAllOrders')"
        class="back-button"
      />

      <div class="flex justify-content-between align-items-center">
        <div class="content-header-left flex flex-column flex-gap-1">
          <div
            class="content-heading-container flex flex-gap-1 align-items-center"
          >
            <OrderOrShipmentTemplateControls
              v-if="authorization.hasAuthority('orderTemplate', 'read')"
              :key="`template-controls-${selectedClient?.naturalId}`"
              v-model:selected-template="selectedTemplate"
              :edited="isDirty"
              :header-text="t('common.newParam', { param: t('orders.orders') })"
              header-icon="file-alt"
              @discard-changes="onOrderTemplateDiscardChanges"
            />
            <template v-else>
              <HeaderIcon icon="file-alt" />
              <h1 id="content-heading" tabindex="-1">
                {{ t('common.newParam', { param: t('orders.orders') }) }}
              </h1>
            </template>

            <TagContainer
              v-model="order.tags"
              :loading="tagOptions.loading"
              :options="tagOptions.data"
              :allow-deletes="canDeleteTags"
              @add="onAddTag"
              @delete="
                requestDeleteConfirmation({
                  message: t('tags.tagWarningParam', {
                    param: t('common.ordersAndShipments'),
                  }),
                  accept: () => onDelete($event),
                })
              "
            />
          </div>
        </div>

        <RygenButton
          v-if="isAiOrderEntryEnabled"
          as="router-link"
          mode="secondary"
          :to="{
            name: 'aiOrderEntry',
            query: { clientId: selectedClient?.id },
          }"
          :label="t('aiOrderEntry.aiOrderEntry')"
          icon="pi pi-file-import"
        />
      </div>
    </template>

    <template #toolbar>
      <EntryToolbar
        v-model:batch-amount="batchCreate.numOrders"
        :save-button-label="
          !selectedClient
            ? t('common.createParam', { param: t('orders.orders') })
            : t('common.createParamForClient', {
                param: t('orders.orders'),
                client: selectedClient?.name,
              })
        "
        :disabled="!selectedClient"
        :loading="isAnyLoading"
        allow-batch
        @save="onSave"
      />
    </template>

    <BlockUI :blocked="isSaving" full-screen />
    <BlockUI
      :key="`main-content-${selectedClient?.naturalId}`"
      :blocked="!selectedClient"
    >
      <StopEntrySection id="stops" v-model="order.stops" />

      <LineItemEntrySection
        id="line-items"
        v-model="order.lineItems"
        type="Order"
        :disable-default-reference-numbers="!!selectedTemplate"
      />

      <BillToEdit
        id="bill-to"
        v-model="order.billTo"
        class="entry-section"
        expanded
      />

      <div class="entry-section flex flex-gap-1">
        <div class="flex flex-column flex-gap-1 w-6">
          <BasicInformationEntrySection id="information" v-model="order" />

          <FreightAllowanceSection
            id="freight-allowance"
            v-model="order.freightAllowance"
            :entity="order"
            :selected-client="selectedClient"
          />
        </div>

        <div class="flex flex-column flex-gap-1 w-6">
          <Section
            id="accessorials"
            :header="t('referenceData.accessorials', 2)"
          >
            <Card>
              <template #content>
                <AccessorialsEdit
                  v-model="order.requestedAccessorials"
                  class="w-6"
                />
              </template>
            </Card>
          </Section>

          <Section
            id="reference-numbers"
            :header="t('referenceData.referenceNumbers', 2)"
          >
            <Card>
              <template #content>
                <ReferenceNumbersEdit
                  v-model="order.referenceNumbers"
                  :errors="v$.referenceNumbers.$errors"
                  :levels="[ReferenceNumberTypeLevel.ORDER]"
                />
              </template>
            </Card>
          </Section>

          <Section
            v-if="$hasAuthority('glCode', 'manualInput')"
            id="gl-codes"
            :header="t('glCodes.glCodes', 2)"
          >
            <Card>
              <template #content>
                <GLCodesEdit
                  v-model="order.glCodes"
                  :errors="v$.glCodes.$errors"
                  class="w-6"
                />
              </template>
            </Card>
          </Section>
        </div>
      </div>
    </BlockUI>

    <AddressValidationResultModal
      :key="`address-validation-result-${selectedClient?.naturalId}`"
      v-model:visible="addressValidation.showModal"
      :class="[
        addressValidation.currentLocation,
        { 'bill-to': addressValidation.currentLocation === 'billTo' },
      ]"
      :validation-result="addressValidation.validationResult"
      @accept-corrected-address="updateLocationWithValidationResult(true)"
      @keep-original-address="updateLocationWithValidationResult(false)"
    />

    <BatchCreateSuccessModal
      :key="`batch-create-success-${selectedClient?.naturalId}`"
      :visible="batchCreate.showModal"
      :ids="batchCreate.orderIds"
      type="Order"
      @update:visible="onModalVisibilityChange"
    />

    <ChangeDomainModal
      v-model:visible="isChangeDomainModalVisible"
      :selected-domain="selectedClient"
      :selectable-domain-types="[DomainType.Client]"
      :domain-options="clients.data"
      :header="t('common.selectParam', { param: t('clients.clients') })"
      position="center"
      closable
      @change-domain="onClientChange"
    >
      <template v-if="isDirty && !!selectedClient" #message>
        <Message severity="warn" icon="pi pi-exclamation-triangle">
          {{
            t('common.selectingClientWillResetParam', {
              param: t('orders.orders'),
            })
          }}
        </Message>
      </template>
    </ChangeDomainModal>
  </Content>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import { useFlag } from '@unleash/proxy-client-vue';
import axios from 'axios';
import isEqual from 'lodash/isEqual';
import type { Ref } from 'vue';
import {
  computed,
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import authorization from '@rygen/rygen-ui/authorization';
import {
  useDeleteConfirmation,
  useToast,
  useUid,
  useUnsavedChangesConfirmation,
  useValidations,
} from '@rygen/rygen-ui/composables';
import {
  AddressValidationResult,
  DailyHoursOfOperation,
  DomainReference,
  DomainTree,
  DomainType,
  ErrorResponse,
  HoursOfOperation,
  Location,
  ReferenceNumberTypeLevel,
  ShippingPointDetails,
  ShippingPointType,
  Tag,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { useNewOrderWithDefaults } from '@/composables';
import {
  EmbeddableLocation,
  Order,
  OrderLineItem,
  OrderOrShipmentTemplate,
  OrderShippingPoint,
} from '@/model';
import { useClientStore } from '@/stores/Client';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useLocationGroupStore } from '@/stores/LocationGroup';
import { useMessageStore } from '@/stores/Message';
import { useParentEntityStore } from '@/stores/ParentEntity';
import { useTagStore } from '@/stores/Tag';
import { isBadRequest } from '@/util';

import AccessorialsEdit from '@/components/accessorial/AccessorialsEdit.vue';
import BasicInformationEntrySection from '@/components/entry/BasicInformationEntrySection.vue';
import StopEntrySection from '@/components/entry/StopEntrySection.vue';
import FreightAllowanceSection from '@/components/FreightAllowance/FreightAllowanceSection.vue';
import GLCodesEdit from '@/components/glCode/GLCodesEdit.vue';
import LineItemEntrySection from '@/components/lineItem/LineItemEntrySection.vue';
import BillToEdit from '@/components/location/BillToEdit.vue';
import OrderOrShipmentTemplateControls from '@/components/orderOrShipmentTemplate/OrderOrShipmentTemplateControls.vue';
import ReferenceNumbersEdit from '@/components/referenceNumber/ReferenceNumbersEdit.vue';
import BatchCreateSuccessModal from '@/components/ui/BatchCreateSuccessModal.vue';
import EntryToolbar from '@/components/ui/EntryToolbar.vue';
import AddressValidationResultModal from '@rygen/rygen-ui/components/address/AddressValidationResultModal.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import ClientAvatar from '@rygen/rygen-ui/components/client/ClientAvatar.vue';
import Content from '@rygen/rygen-ui/components/content/Content.vue';
import HeaderIcon from '@rygen/rygen-ui/components/content/HeaderIcon.vue';
import ChangeDomainModal from '@rygen/rygen-ui/components/domain/ChangeDomainModal.vue';
import TagContainer from '@rygen/rygen-ui/components/tag/TagContainer.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import BlockUI from 'primevue/blockui';
import Card from 'primevue/card';
import Message from 'primevue/message';

type LocationKey = Extract<keyof Order, 'origin' | 'destination' | 'billTo'>;

const OWNING_CLIENT_MESSAGE_KEY = 'order-entry-owning-client-message';

const { clientTreeMap, clients, loadClients } = useClientStore();
const { selectedDomain } = useCurrentUserStore();
const { requestDeleteConfirmation } = useDeleteConfirmation();
const { t } = useI18n();
const { resolveLocationGroups } = useLocationGroupStore();
const { addMessage, removeMessage } = useMessageStore();
const { clearParentEntity, setParentEntity } = useParentEntityStore();
const router = useRouter();
const { loadTags, tags, tagOptions, deleteTag } = useTagStore();
const toast = useToast();
const { uid } = useUid('order-entry');

const isAiOrderEntryEnabled = useFlag('corsair-ai-service-order-entry-feat-flag');

const selectedTemplate: Ref<OrderOrShipmentTemplate | undefined> = ref();
const {
  isLoading: isLoadingDefaults,
  isDirty,
  order,
  orderWithDefaults,
} = useNewOrderWithDefaults(selectedTemplate);

const rules = {
  referenceNumbers: {},
  glCodes: {},
};
const { setServerErrors, v$, validate } = useValidations(order, rules, uid);

const isLoading = ref(false);
const isSaving = ref(false);
const isRedirecting = ref(false);
const isChangeDomainModalVisible = ref(false);

useUnsavedChangesConfirmation({
  isDirtyCallback: async () =>
    !!selectedClient.value &&
    !isSaving.value &&
    !isRedirecting.value &&
    isDirty.value,
});

const selectedClient: Ref<DomainTree | undefined> = ref();

const batchCreate = ref({
  showModal: false,
  numOrders: 1,
  orderIds: [] as number[],
});

const addressValidation = ref({
  showModal: false,
  validationResult: new AddressValidationResult(),
  currentLocation: 'origin' as LocationKey,
});

const canDeleteTags = computed((): boolean =>
  authorization.hasAuthority('tag', 'manage'),
);

const isAnyLoading = computed(() => {
  return isLoading.value || isLoadingDefaults.value || isSaving.value;
});

const validateAllLocations = async (): Promise<boolean> => {
  let areAllValid = true;
  if (order.value.origin.location?.requiresValidation) {
    areAllValid &&= await validateLocation(
      order.value.origin.location,
      'origin',
    );
  }
  if (areAllValid && order.value.destination.location?.requiresValidation) {
    areAllValid &&= await validateLocation(
      order.value.destination.location,
      'destination',
    );
  }
  if (areAllValid && order.value.billTo?.location?.requiresValidation) {
    areAllValid &&= await validateLocation(
      order.value.billTo.location,
      'billTo',
    );
  }

  return areAllValid;
};

const onOrderTemplateDiscardChanges = () => {
  order.value = new Order({
    ...(selectedTemplate.value?.templateData as Order),
    owner: selectedClient.value,
  });
  nextTick(() => (orderWithDefaults.value = new Order(order.value)));
};

const saveProductsIfNecessary = async () => {
  await Promise.all(
    order.value.lineItems
      .filter((li) => li.saveToProductList)
      .map(saveProductToProductList),
  );
};

const saveToAddressBookIfNecessary = async () => {
  if (order.value.origin?.saveToAddressBook) {
    const savedLocation = await saveShippingPointToAddressBook(
      order.value.origin,
      ShippingPointType.SHIPPER,
    );
    order.value.origin.id = savedLocation?.id;
  }
  if (order.value.destination?.saveToAddressBook) {
    const savedLocation = await saveShippingPointToAddressBook(
      order.value.destination,
      ShippingPointType.RECEIVER,
    );
    order.value.destination.id = savedLocation?.id;
  }
};

const onSave = async () => {
  if (!(await validate())) {
    return;
  }

  order.value.referenceNumbers = order.value.referenceNumbers.filter(
    (refNum) => refNum?.type?.naturalId && refNum.value,
  );

  try {
    isSaving.value = true;
    if (!(await validateAllLocations())) {
      return;
    }
    await saveToAddressBookIfNecessary();
    await saveProductsIfNecessary();
    if (batchCreate.value.numOrders === 1) {
      const created = await api.order.create(order.value);
      isRedirecting.value = true;
      router
        .push({
          name: 'orderDetail',
          params: {
            orderId: `${created.id}`,
          },
        })
        .then(() =>
          toast.addSuccess({
            detail: t('messages.successSavingParam', {
              param: t('orders.orders'),
            }),
          }),
        )
        .then(() => (isRedirecting.value = false));
    } else {
      batchCreate.value.orderIds = await api.orderBatch.create(
        order.value,
        batchCreate.value.numOrders,
      );
      isRedirecting.value = true;
      batchCreate.value.showModal = true;
    }
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return;
    }

    const errorResponse = e.response?.data as ErrorResponse;
    if (isBadRequest(e)) {
      setServerErrors(errorResponse);
    } else {
      const errorDetail = errorResponse.exceptionMessage;
      console.error('Error saving order', order.value, e);
      toast.addError({
        detail:
          t('errors.savingParam', {
            param: t('orders.orders'),
          }) + (errorDetail ? ` - ${errorDetail}` : ''),
        error: e,
      });
    }
  } finally {
    isSaving.value = false;
  }
};

const saveShippingPointToAddressBook = async (
  shippingPoint: OrderShippingPoint,
  type: ShippingPointType,
): Promise<Location | undefined> => {
  try {
    const dailyHoursOfOperation = new DailyHoursOfOperation({
      open: true,
      from:
        shippingPoint.requestedAppointmentEarliest?.toFormat('HH:mm:ss') ??
        '08:00:00',
      to:
        shippingPoint.requestedAppointmentLatest?.toFormat('HH:mm:ss') ??
        '17:00:00',
    });

    const groups = resolveLocationGroups(
      shippingPoint.location?.locationGroupIds ?? [],
    );

    const location = new Location({
      id: shippingPoint.location?.locationId,
      name: shippingPoint.location?.locationName,
      address: shippingPoint.location?.toAddress(),
      code: shippingPoint.location?.code,
      shippingPointDetails: new ShippingPointDetails({
        ...shippingPoint.shippingPointDetails,
        defaultAccessorials: shippingPoint.requestedAccessorials,
        hoursOfOperation: new HoursOfOperation({
          hoursSameForAllDays: false,
          monday: dailyHoursOfOperation,
          tuesday: dailyHoursOfOperation,
          wednesday: dailyHoursOfOperation,
          thursday: dailyHoursOfOperation,
          friday: dailyHoursOfOperation,
          saturday: new DailyHoursOfOperation({ open: false }),
          sunday: new DailyHoursOfOperation({ open: false }),
        }),
      }),
      groups,
    });
    location.shippingPointDetails?.shippingPointTypes?.push(type);

    return location.id
      ? api.shippingPoint.update(location)
      : api.shippingPoint.save(location);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    const isFiconLocation = !!shippingPoint.location.locationId;
    console.error(
      `Error ${isFiconLocation ? 'updating' : 'saving'} location for shipping point`,
      e,
    );
    toast.addError({
      detail: t(
        isFiconLocation ? 'errors.updatingParam' : 'errors.savingParam',
        { param: t('locations.locations') },
      ),
      error: e,
    });
    return undefined;
  }
};

const saveProductToProductList = async (lineItem: OrderLineItem) => {
  try {
    return lineItem.productId
      ? await api.product.update(lineItem.toProduct())
      : await api.product.save(lineItem.toProduct());
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error(
      `Error ${lineItem.productId ? 'updating' : 'saving'} product`,
      e,
    );
    toast.addError({
      detail: t(
        lineItem.productId ? 'errors.updatingParam' : 'errors.savingParam',
        {
          param: t('products.products', 1),
        },
      ),
      error: e,
    });
  }
};

const validateLocation = async (
  location: EmbeddableLocation,
  currentLocation: LocationKey,
): Promise<boolean> => {
  try {
    isLoading.value = true;
    addressValidation.value.currentLocation = currentLocation;
    addressValidation.value.validationResult =
      await api.shippingPoint.validateAddress(location.toAddress());

    if (addressValidation.value.validationResult.errors?.length) {
      addressValidation.value.showModal = true;
      return false;
    } else {
      location.hasBeenValidated = true;
      return true;
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return false;
    }

    console.error('Error validating address', e);
    return true;
  } finally {
    isLoading.value = false;
  }
};

const updateLocationWithValidationResult = (acceptCorrection: boolean) => {
  const location = order.value[addressValidation.value.currentLocation];
  if (!location) {
    return;
  }

  if (acceptCorrection) {
    location.location = EmbeddableLocation.fromAddress(
      addressValidation.value.validationResult.correctedAddress?.address,
      location.location?.locationName,
      addressValidation.value.validationResult.geocodeResponse?.timeZoneLocale,
    );
  }
  location.location.hasBeenValidated = true;
  addressValidation.value.showModal = false;
  onSave();
};

const onModalVisibilityChange = (visible: boolean) => {
  if (batchCreate.value.showModal && !visible) {
    router
      .push({ name: 'orderList' })
      .then(() => (isRedirecting.value = false));
  }
  nextTick(() => (batchCreate.value.showModal = visible));
};

const onAddTag = (tag: string) => {
  tags.value.data?.push(new Tag({ name: tag }));
};

const onClientChange = (newClient: DomainReference) => {
  selectedClient.value = newClient.id
    ? clientTreeMap.value.get(newClient.id)
    : undefined;
};

const onDelete = (tag: string) => {
  const tagToDelete = tags.value.data?.find((t: Tag) => t.name === tag);
  if (tagToDelete) {
    deleteTag(tagToDelete);
  }
};

const updateSelectedClient = (selectedDomain: DomainReference | undefined) => {
  selectedClient.value =
    selectedDomain?.domainType === DomainType.Client && selectedDomain.id
      ? clientTreeMap.value.get(selectedDomain.id)
      : undefined;

  isChangeDomainModalVisible.value = !selectedClient.value;
};

watch(selectedDomain, async (newVal: DomainReference | undefined) => {
  await loadClients();
  updateSelectedClient(newVal);
});

watch(
  selectedClient,
  async (newVal: DomainTree | undefined) => {
    if (newVal) {
      order.value = new Order({
        ...orderWithDefaults.value,
        owner: newVal?.asDomainReference(),
      });
      setParentEntity(order);
      loadTags({ bypassCache: true });

      addMessage({
        id: OWNING_CLIENT_MESSAGE_KEY,
        severity: 'info',
        text: t('common.assignedToParam', { param: newVal.name }),
        iconSlot: () =>
          h(ClientAvatar, {
            name: newVal.name,
            logo: newVal.branding?.avatar?.signedUrl,
          }),
        onClick: () => (isChangeDomainModalVisible.value = true),
      });
    }
  },
  { immediate: true },
);

watch(isChangeDomainModalVisible, (newVal: boolean, oldVal: boolean) => {
  if (oldVal && !newVal && !selectedClient.value) {
    router.back();
  }
});

watch(
  selectedTemplate,
  (
    newVal: OrderOrShipmentTemplate | undefined,
    oldVal: OrderOrShipmentTemplate | undefined,
  ) => {
    // Verify this is a different template, not a template changing default
    // status
    if (newVal?.id === oldVal?.id) {
      return;
    }

    if (!isEqual(order.value, newVal?.templateData)) {
      order.value = new Order({
        ...(newVal?.templateData as Order),
        owner: selectedClient.value,
      });
      nextTick(() => v$.value.$reset());
    }
  },
  { immediate: true },
);

watch(
  () => batchCreate.value.numOrders,
  (newVal: number | null) => {
    if (!newVal) {
      batchCreate.value.numOrders = 1;
    }
  },
);

onMounted(async () => {
  await loadClients();
  updateSelectedClient(selectedDomain.value);
});

onBeforeUnmount(() => {
  clearParentEntity();
  removeMessage(OWNING_CLIENT_MESSAGE_KEY);
});
</script>

<style>
.order-entry {
  --p-card-body-padding: var(--sp-3) var(--sp-6);

  .content-header {
    .back-button + div {
      /* this is the row that spans the full width of the page */

      .content-header-left {
        align-items: flex-start;
        width: fit-content;
      }
    }
  }

  .content-container {
    .detail-section {
      padding: 0;
      border: none;
    }

    .entry-section + .entry-section {
      margin: var(--sp-6) 0 0;
    }

    .line-item-summary-card {
      --p-card-body-padding: var(--sp-6);
    }

    .freight-allowance.suggestion-button {
      margin-top: var(--sp-2);

      .p-button-label {
        width: 100%;
        text-decoration: none !important;
      }
    }
  }

  .p-blockui-mask.p-overlay-mask {
    background-color: rgb(from var(--p-surface-100) r g b / 0.65);
    /*
    There's a bug with the PrimeVue component where the animation class isn't
    removed so it prevents the style above from being applied
    */
    animation: none;
  }
}
</style>
